import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGlobalState } from "../../GlobalContext";
import { isVisible } from "../config/visibilityRules";
import { getRecordDefinition } from "./recordDefinitions";
import { getLastModifiedLabel } from "./lastModifiedLabel";
import { getChangedAttributes } from "./entityPagePayload";
import { EntityPageContext } from "./EntityPageContext";
import { useEntityPagePermissions } from "./useEntityPagePermissions";
import { useEntityRecord } from "./useEntityRecord";
import { useEntityPageLayout } from "./useEntityPageLayout";
import { useEntityPageValidation } from "./useEntityPageValidation";
import { useEntityPageSave } from "./useEntityPageSave";
import { useCloseRoute } from "./useCloseRoute";
import { EntityPageUnsavedChanges } from "./EntityPageUnsavedChanges";
import { ConfirmationModal } from "../elements/ConfirmationModal";
import {
  EntityPageProviderProps,
  EntityPageState,
} from "./EntityPageProvider.types";

/**
 * Owns everything stateful about an entity page - loading, editing, saving,
 * form selection - so the page's components are only markup.
 */
export function EntityPageProvider({
  entityName,
  children,
}: EntityPageProviderProps) {
  const definition = getRecordDefinition(entityName);
  const { isAllowed, canEdit } = useEntityPagePermissions(definition);
  const [informationVisible, setInformationVisible] = useState(false);

  const {
    id,
    record,
    originalRecord,
    setOriginalRecord,
    loading,
    error,
    notFound,
    formId,
    resolvedView,
    readRecord,
    applyRead,
    skipNextFetch,
    settleView,
    onRefresh,
    onFieldChange,
    onRelatedRecordsChange,
    onSelectForm,
  } = useEntityRecord({ entityName, definition, isAllowed });

  // A new record goes through its wizard, if its page has one, before the full page.
  const [wizardDone, setWizardDone] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const isNew = !record?.id && !definition?.readMethod;

  const tenantConfig = useGlobalState()?.config;
  const { rows, views, fieldLabels, fieldValidatorKeys, wizard } =
    useEntityPageLayout({
      definition,
      formId,
      resolvedView,
      record,
      tenantConfig,
      wizard: { enabled: isNew && !wizardDone, step: wizardStep },
    });
  const canEditRecord =
    canEdit &&
    isVisible(definition?.editableWhen, record ?? {}, tenantConfig ?? {});

  const { formRef, fieldErrors, validationAttempts, validate, resetValidation } =
    useEntityPageValidation({
      fieldLabels,
      fieldValidatorKeys,
      record,
      id,
      formId,
      stepOnly: Boolean(wizard),
    });

  // A different record, or a different view of one, is a fresh start.
  // Only a new record goes back to its wizard; an id arriving from a create keeps it finished.
  useEffect(() => {
    setInformationVisible(false);

    if (!id) {
      setWizardDone(false);
      setWizardStep(0);
    }
  }, [id, formId]);

  const onWizardContinue = useCallback(() => {
    if (!wizard || !validate()) {
      return;
    }

    // The next step starts clean, rather than showing errors for what is not yet filled in.
    resetValidation();

    if (wizard.step < wizard.stepCount - 1) {
      setWizardStep(wizard.step + 1);
    } else {
      setWizardDone(true);
    }
  }, [wizard, validate, resetValidation]);


  const onWizardBack = useCallback(() => {
    resetValidation();
    setWizardStep((step) => Math.max(0, step - 1));
  }, [resetValidation]);

  const goToCloseRoute = useCloseRoute(definition);
  const allowNavigationRef = useRef(false);

  const { savingAction, onSave, saveConfirmation, confirmSave, cancelSave } =
    useEntityPageSave({
      entityName,
      definition,
      record,
      originalRecord,
      rows,
      validate,
      readRecord,
      applyRead,
      skipNextFetch,
      settleView,
      setOriginalRecord,
      goToCloseRoute,
      allowNavigationRef,
    });

  const isDirty =
    Object.keys(getChangedAttributes(originalRecord, record ?? {})).length > 0;

  // Switching view re-reads the record, so with unsaved edits it waits to be confirmed.
  const [pendingFormId, setPendingFormId] = useState<string | null>(null);

  const onSelectFormGuarded = useCallback(
    (key: string) => {
      if (key === formId) {
        return;
      }

      if (isDirty) {
        setPendingFormId(key);
      } else {
        onSelectForm(key);
      }
    },
    [formId, isDirty, onSelectForm]
  );

  const switchForm = useCallback(
    (key: string) => {
      setPendingFormId(null);
      onSelectForm(key);
    },
    [onSelectForm]
  );

  const value = useMemo<EntityPageState>(
    () => ({
      entityName,
      definition,
      record,
      originalRecord,
      loading,
      error,
      notFound,
      isAllowed,
      canEdit: canEditRecord,
      // A record read by its own method is the organisation's one, so never new.
      isNew,
      isDirty,
      isSaving: savingAction !== null,
      savingAction,
      informationVisible,
      formId,
      views,
      // No view at all: the default deactivated, and nothing stored that this record
      // and user qualify for. A real state, which the page explains.
      hasView: resolvedView === null || resolvedView.hasView,
      rows,
      wizard,
      onWizardContinue,
      onWizardBack,
      fieldErrors,
      validationAttempts,
      lastModifiedLabel: getLastModifiedLabel(record),
      formRef,
      onFieldChange,
      onRelatedRecordsChange,
      onSelectForm: onSelectFormGuarded,
      onToggleInformation: () =>
        setInformationVisible((previous) => !previous),
      onRefresh,
      onSave,
      onCancel: goToCloseRoute,
    }),
    [
      entityName,
      definition,
      record,
      originalRecord,
      isDirty,
      loading,
      error,
      notFound,
      isAllowed,
      canEditRecord,
      savingAction,
      informationVisible,
      formId,
      views,
      resolvedView,
      rows,
      isNew,
      wizard,
      onWizardContinue,
      onWizardBack,
      fieldErrors,
      validationAttempts,
      formRef,
      onFieldChange,
      onRelatedRecordsChange,
      onSelectFormGuarded,
      onRefresh,
      onSave,
      goToCloseRoute,
    ]
  );

  return (
    <EntityPageContext.Provider value={value}>
      {children}
      <EntityPageUnsavedChanges
        isDirty={isDirty}
        save={() => (wizard ? Promise.resolve(false) : onSave(false))}
        allowNavigationRef={allowNavigationRef}
        pendingFormId={pendingFormId}
        onSwitch={switchForm}
        onCancelSwitch={() => setPendingFormId(null)}
      />
      {saveConfirmation && (
        <ConfirmationModal
          modalTitle={saveConfirmation.title}
          confirmText={saveConfirmation.confirmText ?? "Save"}
          isSubmitting={savingAction !== null}
          onClose={cancelSave}
          onConfirm={confirmSave}
        >
          {saveConfirmation.paragraphs.map((paragraph, index) => (
            <p
              key={paragraph}
              className={
                index === saveConfirmation.paragraphs.length - 1 ? "mb-0" : ""
              }
            >
              {paragraph}
            </p>
          ))}
        </ConfirmationModal>
      )}
    </EntityPageContext.Provider>
  );
}

export default EntityPageProvider;
